import { useEffect, useRef, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "@/hooks/useAuth";
import { SEO } from "@/components/SEO";
import { GetAccessModal } from "@/components/GetAccessModal";
import { MarketingNav, MarketingFooter, MarketingFaq } from "@/components/marketing/MarketingChrome";
import { RouteMap } from "@/components/marketing/RouteMap";
import { BusyCalendar, TOTAL_LESSONS, TOTAL_LESSON_MINUTES } from "@/components/marketing/BusyCalendar";
import { WaitlistForm } from "@/components/marketing/WaitlistForm";
import { useMarketingMotion } from "@/components/marketing/useMarketingMotion";
import { useMarketingTheme } from "@/components/marketing/useMarketingTheme";
import { captureEvent } from "@/lib/posthog";
import "@/styles/marketing.css";

/** Targets that rotate through the headline. Technical and business skills only. */
const ROTATING_TARGETS = [
  "DATA ANALYSIS.",
  "AWS CERTS.",
  "CYBERSECURITY.",
  "PROJECT MGMT.",
  "SQL & EXCEL.",
];

const FAQ_ENTRIES = [
  {
    q: "What happens after I join?",
    a: "We open the doors in small groups. When your turn comes, we'll email you personally to get you started. No spam in the meantime, promise.",
  },
  {
    q: "When do individuals get access?",
    a: "We're onboarding our first pilot teams now, and individuals join as we open the doors wider. The waitlist is the queue, and telling us your target helps us know what to build first.",
  },
  {
    q: "What does it cost?",
    a: "Early access works on early terms, and we'll talk about it openly when we reach out. There's no pricing page yet and we won't pretend there is.",
  },
  {
    q: "I lead a team. Is this for us?",
    a: "Very much. Teams get their own thing: Learning Sprints built from your company's own materials, with per-person proof. See the For Teams page, or pick \"For my team\" on the form.",
  },
];

const FEATURES = [
  {
    title: "Minutes, not marathons",
    body: "Lessons take a few minutes each. Small enough to actually happen today.",
    icon: (
      <svg viewBox="0 0 24 24"><circle cx="12" cy="12" r="9" /><path d="M12 7v5l3 3" /></svg>
    ),
  },
  {
    title: "Your shortest route",
    body: "Mapped from your goal and what you already know. Nothing you don't need.",
    icon: (
      <svg viewBox="0 0 24 24"><path d="M4 19c4-1 6-3 8-7s4-6 8-7" /><circle cx="4" cy="19" r="2" /><path d="M20 5l-4 0M20 5l0 4" /></svg>
    ),
  },
  {
    title: "A check in every lesson",
    body: "Prove it as you go. Passing feels different from just watching.",
    icon: <svg viewBox="0 0 24 24"><path d="M4 12l5 5L20 6" /></svg>,
  },
  {
    title: "Progress you can see",
    body: "Your route fills in as you move. You always know where you stand.",
    icon: (
      <svg viewBox="0 0 24 24"><path d="M3 17l6-6 4 4 8-8" /><path d="M21 7v4h-4" /></svg>
    ),
  },
  {
    title: "No filler courses",
    body: "Skip what you already know. Go deep only where it counts.",
    icon: <svg viewBox="0 0 24 24"><path d="M13 3H6v18h12V8z" /><path d="M13 3v5h5" /></svg>,
  },
  {
    title: "A finish line",
    body: "Routes are time-boxed. Learning that ends, with something to show.",
    icon: (
      <svg viewBox="0 0 24 24"><rect x="3" y="4" width="18" height="16" rx="2" /><path d="M3 9h18M8 4v5" /></svg>
    ),
  },
];

export default function Landing() {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, isLoading } = useAuth();
  const [accessModalOpen, setAccessModalOpen] = useState(false);
  const [initialAccessKey, setInitialAccessKey] = useState<string | undefined>(undefined);
  const [goalDraft, setGoalDraft] = useState("");
  const [prefilledTarget, setPrefilledTarget] = useState<string | undefined>(undefined);
  const [rotatingIndex, setRotatingIndex] = useState(0);
  const [rotatingPhase, setRotatingPhase] = useState<"in" | "out">("in");
  const rotatingWord = ROTATING_TARGETS[rotatingIndex];
  const heroRef = useRef<HTMLElement>(null);

  useMarketingTheme();
  useMarketingMotion(!isLoading);

  // An invited user arriving with ?access_key=… still lands straight in the key dialog.
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const accessKey = params.get("access_key");
    if (accessKey) {
      setInitialAccessKey(accessKey);
      setAccessModalOpen(true);
      navigate("/", { replace: true });
    }
  }, [location.search, navigate]);

  useEffect(() => {
    if (!isLoading && user) navigate("/app");
  }, [user, isLoading, navigate]);

  useEffect(() => {
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;
    const interval = window.setInterval(() => {
      setRotatingPhase("out");
      window.setTimeout(() => {
        setRotatingIndex((current) => (current + 1) % ROTATING_TARGETS.length);
        setRotatingPhase("in");
      }, 320);
    }, 2600);
    return () => window.clearInterval(interval);
  }, []);

  useEffect(() => {
    captureEvent("marketing_page_viewed", { page: "home", audience: "individual" });
  }, []);

  const handleGoalSubmit = (event: React.FormEvent) => {
    event.preventDefault();
    const value = goalDraft.trim();
    captureEvent("hero_goal_submitted", { page: "home", has_value: value.length > 0 });
    // A trailing space keeps the value distinct on repeat submits so the form
    // effect re-runs and focus returns to the target field.
    setPrefilledTarget(value ? value : " ");
    document.getElementById("join")?.scrollIntoView({ behavior: "smooth" });
  };

  if (isLoading) {
    return (
      <div className="mk" style={{ display: "grid", placeItems: "center", minHeight: "100vh" }}>
        <SEO noIndex />
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary" />
      </div>
    );
  }

  return (
    <div className="mk">
      <SEO
        title="The shortest route to the skills you want"
        description="BrainWrk maps the shortest route from where you are to the skill you're aiming for, in bite-sized lessons with real checks. Join the waitlist."
        keywords="learn data analysis, AWS certification, cybersecurity training, bite-sized learning, skills roadmap, learning path"
        canonicalUrl="/"
        ogType="website"
        structuredData={{
          "@context": "https://schema.org",
          "@graph": [
            {
              "@type": "Organization",
              "@id": "https://brainwrk.ai/#organization",
              name: "BrainWrk",
              url: "https://brainwrk.ai",
              logo: {
                "@type": "ImageObject",
                url: "https://brainwrk.ai/brainwrk-logo.png",
              },
              description:
                "BrainWrk maps the shortest route from where a learner is to the skill they're aiming for, delivered as bite-sized lessons with built-in checks.",
            },
            {
              "@type": "WebSite",
              "@id": "https://brainwrk.ai/#website",
              url: "https://brainwrk.ai",
              name: "BrainWrk",
              publisher: { "@id": "https://brainwrk.ai/#organization" },
            },
          ],
        }}
      />

      <MarketingNav current="home" />

      <header className="shell hero" ref={heroRef}>
        <div className="hero-top">
          <h1 className="giant">
            The shortest route to
            <span className="rot-wrap">
              <span className={`rot-word rot-${rotatingPhase}`} key={rotatingWord}>
                {rotatingWord}
              </span>
            </span>
          </h1>
          <div className="hero-side">
            <p className="lead">
              Bite-sized lessons, mapped from where you are to where you're going. Minutes a day,
              with <b>proof you're getting there.</b>
            </p>
            <form className="goalbox" onSubmit={handleGoalSubmit}>
              <input
                type="text"
                value={goalDraft}
                onChange={(event) => setGoalDraft(event.target.value)}
                placeholder="What do you want to get better at?"
                aria-label="What do you want to get better at?"
                data-testid="input-hero-goal"
              />
              <button type="submit" aria-label="Continue to the waitlist" data-testid="button-hero-goal">
                →
              </button>
            </form>
            <div className="goal-hint">Type your goal · it pre-fills the waitlist</div>
            <div className="proofline">
              <b>●</b>
              <span>We're opening the doors in small groups. The waitlist is the queue.</span>
            </div>
          </div>
        </div>

        <div className="hero-panel" id="heroPanel">
          <div className="float-chip fc1"><span className="dot" />Today · 6 minutes</div>
          <div className="float-chip fc2"><span className="dot" />4 checks passed this week</div>
          <div className="panel-card">
            <RouteMap />
            <div className="rhint">
              Hover a green stop for its lessons ·{" "}
              <b>the grey line loops back on itself, the green one never does</b>
            </div>
          </div>
        </div>
      </header>
      <div className="hero-base" />

      <div className="shell stackzone" id="how">
        <div className="stack-card k1 reveal" id="stackTop">
          <div>
            <div className="sk-kick">Bite-sized, every day</div>
            <h3>Lessons that fit inside a coffee break.</h3>
            <p>
              Your week is already full. Short lessons slide into the gaps you already have,
              between the standup and the client call, without moving a single meeting.
            </p>
            <span className="cal-badge">
              ✓ {TOTAL_LESSONS} lessons · {TOTAL_LESSON_MINUTES} minutes this week · zero meetings moved
            </span>
          </div>
          <BusyCalendar />
        </div>
        <div className="stack-card k2 reveal" id="stackBottom">
          <div>
            <div className="sk-kick">Proof, not vibes</div>
            <h3>Watch yourself actually get there.</h3>
            <p>
              Every check you pass moves you along your route. You always know how far you've come
              and what's next, no guessing.
            </p>
          </div>
          <div className="k2-art">
            <div className="pr-row">
              <div className="pr-lab"><span>Route progress</span><b>64%</b></div>
              <div className="pr-bar"><i style={{ width: "64%" }} /></div>
            </div>
            <div className="pr-row">
              <div className="pr-lab"><span>Checks passed</span><b>11 of 14</b></div>
              <div className="pr-bar"><i style={{ width: "78%" }} /></div>
            </div>
            <div className="pr-row">
              <div className="pr-lab"><span>Today's step</span><b className="am">6 MIN</b></div>
              <div className="pr-bar am"><i style={{ width: "35%" }} /></div>
            </div>
            <div className="pr-foot">
              Next up: <b>Practice · Pivot tables on real data</b>
            </div>
          </div>
        </div>
      </div>
      <div className="spacer-after-stack" />

      <div className="shell">
        <div className="canvas reveal">
          <div className="fgrid-wrap">
            <h2 className="fgrid-head">
              Small steps.<br />
              <span className="grn">Real proof.</span>
            </h2>
            <div className="fgrid">
              {FEATURES.map((feature) => (
                <div className="feat" key={feature.title}>
                  <div className="f-ic" aria-hidden="true">{feature.icon}</div>
                  <h4>{feature.title}</h4>
                  <p>{feature.body}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      <div className="shell band">
        <div className="band-card reveal">
          <div>
            <div className="band-kick" style={{ color: "var(--acc)" }}>For teams</div>
            <h3>Leading a team? Sprints are for you.</h3>
            <p>
              BrainWrk turns your company's own decks, docs and SOPs into time-boxed team Learning
              Sprints, with per-person competency proof back to you.
            </p>
          </div>
          <Link className="btn pri" to="/teams" data-testid="link-teams-page">
            For Teams →
          </Link>
        </div>
      </div>

      <div className="shell reveal" id="join">
        <div className="finale">
          <div className="kick grn">Doors open in small groups</div>
          <h2 className="giant-f">
            Join the <span className="grn">waitlist.</span>
          </h2>
          <p className="subs">
            Tell us what you're aiming for and we'll reach out when your turn comes.
          </p>
        </div>
        <WaitlistForm
          defaultAudience="individual"
          prefilledTarget={prefilledTarget}
          sourcePage="home"
          onAccessKeyClick={() => setAccessModalOpen(true)}
        />
      </div>

      <MarketingFaq entries={FAQ_ENTRIES} />

      <MarketingFooter tagline="the shortest route from where you are to competency." />

      <GetAccessModal
        open={accessModalOpen}
        onOpenChange={(open) => {
          setAccessModalOpen(open);
          if (!open) setInitialAccessKey(undefined);
        }}
        initialAccessKey={initialAccessKey}
        onJoinWaitlist={() => document.getElementById("join")?.scrollIntoView({ behavior: "smooth" })}
      />
    </div>
  );
}
